import React, { useMemo } from 'react';

import * as style from './PackCard.css';
import { InfoIcon } from '../../assets/icons/info';
import { formatNumber } from '../../utils';
import { AvailableLabel } from '../AvailableLabel';
import { CardButtons } from '../CardButtons';
import { RewardBadges } from '../RewardBadges';
import { useStoreCard } from '../StoreCardWrapper/StoreCardContext';

import type { Bundle, Item } from '@site-builder/store-sdk';

const WEB_EXCLUSIVE_URL =
  'https://cdn.xsolla.net/image-proxy/rs:fit:1920:0/f:webp/storage/merchant-bucket-prod/files/uploaded/sitebuilder/439172/029837f394f48a8e56df220d43229140.png@webp';
const WEB_ONLY_URL =
  'https://cdn.xsolla.net/image-proxy/rs:fit:1920:0/f:webp/storage/merchant-bucket-prod/files/uploaded/sitebuilder/439172/409ab8ce5017df24273ddbfc05e35b35.png@webp';

interface PackCardProps {
  readonly data: Bundle;
  readonly onCartClick?: () => void;
}

export const PackCard = React.memo(({ data, onCartClick }: PackCardProps) => {
  const bundle = data;

  const loyaltyAmount = useMemo(() => {
    const reward = bundle.loyaltyRewards?.[0];
    return reward ? reward.amount : 0;
  }, [bundle]);

  const { hasLimit, availableAmount, maxAvailableAmount } = useStoreCard();

  const webBadgeType = useMemo(() => {
    if (!bundle.attributes?.length) return null;
    if (bundle.attributes.some((attr) => attr.externalId === 'web_exclusive'))
      return 'web_exclusive';
    if (bundle.attributes.some((attr) => attr.externalId === 'web_only'))
      return 'web_only';
    return null;
  }, [bundle]);

  const webBonusPercent = useMemo(() => {
    if (!bundle.attributes?.length) return null;
    const bonusAttr = bundle.attributes.find((attr) =>
      attr.externalId.startsWith('value_bonus_'),
    );
    if (!bonusAttr) return null;
    const valueEntry = bonusAttr.values[0];
    return (
      valueEntry?.value ?? bonusAttr.externalId.replace('value_bonus_', '')
    );
  }, [bundle]);

  const itemsCount = useMemo(() => {
    if (bundle.type === 'bundle') {
      return bundle.content.length;
    }
    return 1;
  }, [bundle]);

  const getContentClassName = () => {
    const baseClass = style.content;

    switch (itemsCount) {
      case 2:
        return `${baseClass} ${style.contentWith2Items}`;
      case 3:
        return `${baseClass} ${style.contentWith3Items}`;
      case 4:
        return `${baseClass} ${style.contentWith4Items}`;
      case 5:
        return `${baseClass} ${style.contentWith5Items}`;
      default:
        return baseClass;
    }
  };

  const getContentItemClassName = () => {
    if (itemsCount === 5) {
      return `${style.contentItem} ${style.contentItem5Items}`;
    }
    return style.contentItem;
  };

  const renderContent = () => {
    if (bundle.type === 'bundle') {
      const items = bundle.content;

      return (
        <>
          {items.map(
            (
              {
                originalItem: item,
                quantity,
              }: {
                originalItem: Item;
                quantity: number;
              },
              index,
            ) => (
              <div key={item.sku} className={getContentItemClassName()}>
                <img
                  className={style.contentItemImage}
                  src={item.imageUrl ?? ''}
                  alt={item.name}
                />
                <span className={style.contentItemAmount}>
                  {formatNumber(quantity)}
                </span>
                {index === items.length - 1 && webBadgeType && (
                  <div
                    className={
                      webBadgeType === 'web_exclusive'
                        ? style.exclusiveBadge
                        : style.webOnlyBadge
                    }
                  >
                    <img
                      src={
                        webBadgeType === 'web_exclusive'
                          ? WEB_EXCLUSIVE_URL
                          : WEB_ONLY_URL
                      }
                      alt={
                        webBadgeType === 'web_exclusive'
                          ? 'Web Exclusive'
                          : 'Web Only'
                      }
                    />
                  </div>
                )}
              </div>
            ),
          )}
        </>
      );
    }

    return (
      <div className={style.contentItem}>
        <img
          className={style.contentItemImage}
          src={data.imageUrl ?? ''}
          alt={data.name}
        />
        <span className={style.contentItemAmount}>1</span>
        {webBadgeType && (
          <div
            className={
              webBadgeType === 'web_exclusive'
                ? style.exclusiveBadge
                : style.webOnlyBadge
            }
          >
            <img
              src={
                webBadgeType === 'web_exclusive'
                  ? WEB_EXCLUSIVE_URL
                  : WEB_ONLY_URL
              }
              alt={
                webBadgeType === 'web_exclusive' ? 'Web Exclusive' : 'Web Only'
              }
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={style.block}>
      <button
        className={style.infoButton}
        type='button'
        aria-label='Info'
        onClick={onCartClick}
      >
        <InfoIcon size={24} />
      </button>
      <div className={`${style.blockInner} msm-frame msm-frame--brown`}>
        <div className='msm-frame__corners'></div>
        <div className={style.about}>
          <h2
            className={`${style.title} msm-frame__title msm-frame__title--wooden`}
          >
            {data.name}
          </h2>
        </div>

        <div className={style.contentWrapper}>
          <div className={getContentClassName()}>{renderContent()}</div>
          <CardButtons />
          <div className={style.bottomWrapper}>
            <RewardBadges loyaltyPoints={loyaltyAmount} />
            {hasLimit && (
              <AvailableLabel
                available={availableAmount}
                total={maxAvailableAmount}
              />
            )}
          </div>
        </div>
        <div className={`${style.msmBadgeWrapper} msm-badge-wrapper`}>
          {webBonusPercent && (
            <div className='msm-badge msm-badge--green'>
              <p className='msm-badge__value'>+{webBonusPercent}%</p>
              <p className='msm-badge__label'>Value</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

PackCard.displayName = 'PackCard';
